import { For, Show, createSignal, createEffect } from "solid-js";
import socket from "./socket";

interface Snapshot {
    id: number;
    type: string;
    createdAt: string;
    createdBy: string;
    description: string;
    dataSize: number;
}

interface ProjectHistoryProps {
    project: {
        id: number;
        name: string;
    };
}

export default function ProjectHistory(props: ProjectHistoryProps) {
    const [history, setHistory] = createSignal<Snapshot[]>([]);
    const [loading, setLoading] = createSignal(false);
    const [showCreateDialog, setShowCreateDialog] = createSignal(false);
    const [snapshotDescription, setSnapshotDescription] = createSignal('');
    const [restoring, setRestoring] = createSignal(false);

    const loadHistory = () => {
        setLoading(true);
        socket.emit('project-get-history', { project_id: props.project.id });
    };

    const createSnapshot = () => {
        const description = snapshotDescription().trim() || 'Snapshot manuel';
        socket.emit('project-create-snapshot', {
            project_id: props.project.id,
            description: description
        });
        setSnapshotDescription('');
        setShowCreateDialog(false);
    };

    const restoreSnapshot = (snapshotId: number) => {
        if (!confirm('Êtes-vous sûr de vouloir restaurer ce snapshot ? Cette action créera une sauvegarde automatique avant la restauration.')) {
            return;
        }
        setRestoring(true);
        socket.emit('project-restore-snapshot', {
            project_id: props.project.id,
            snapshotId: snapshotId
        });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'auto': return 'Automatique';
            case 'manual': return 'Manuel';
            case 'before_restore': return 'Avant restauration';
            default: return type;
        }
    };

    const getTypeBadgeClass = (type: string) => {
        switch (type) {
            case 'auto': return 'bg-secondary-subtle text-secondary border border-secondary-subtle';
            case 'manual': return 'bg-primary-subtle text-primary border border-primary-subtle';
            case 'before_restore': return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
            default: return 'bg-light text-dark border';
        }
    };

    // Gestionnaires d'événements socket
    createEffect(() => {
        const handleProjectHistory = (data: any) => {
            if (data.project_id === props.project.id) {
                setHistory(data.history || []);
                setLoading(false);
            }
        };

        const handleSnapshotCreated = (data: any) => {
            if (data.project_id === props.project.id) {
                if (data.success) {
                    loadHistory();
                } else {
                    alert('Erreur lors de la création du snapshot : ' + data.error);
                }
            }
        };

        const handleRestoreCompleted = (data: any) => {
            if (data.project_id === props.project.id) {
                setRestoring(false);
                if (data.success) {
                    alert('Restauration réussie ! La page va se recharger.');
                    window.location.reload(); // Recharger la page pour refléter les changements
                } else {
                    alert('Erreur lors de la restauration : ' + data.error);
                }
            }
        };

        const handleProjectRestored = (data: any) => {
            if (data.project_id === props.project.id) {
                loadHistory(); // Recharger l'historique
            }
        };

        socket.on('project-history', handleProjectHistory);
        socket.on('snapshot-created', handleSnapshotCreated);
        socket.on('restore-completed', handleRestoreCompleted);
        socket.on('project-restored', handleProjectRestored);

        // Charger l'historique au montage
        loadHistory();

        // Nettoyage
        return () => {
            socket.off('project-history', handleProjectHistory);
            socket.off('snapshot-created', handleSnapshotCreated);
            socket.off('restore-completed', handleRestoreCompleted);
            socket.off('project-restored', handleProjectRestored);
        };
    });

    return (
        <div class="card-panel">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-2 border-bottom gap-2">
                <div>
                    <h3 class="h4 fw-bold text-dark mb-1">Historique des versions & Sauvegardes</h3>
                    <p class="text-muted small mb-0">Consultez l'historique des modifications et restaurez une version précédente du projet.</p>
                </div>
                <div class="d-flex gap-2">
                    <button 
                        class="btn btn-primary d-inline-flex align-items-center gap-2 shadow-sm" 
                        onClick={() => setShowCreateDialog(true)}
                        disabled={restoring()}
                    >
                        <i class="bi bi-plus-circle"></i>
                        <span>Créer un point de sauvegarde</span>
                    </button>
                    <button 
                        class="btn btn-outline-secondary d-inline-flex align-items-center gap-2" 
                        onClick={loadHistory}
                        disabled={loading() || restoring()}
                    >
                        <i class="bi bi-arrow-clockwise"></i>
                        <span>Actualiser</span>
                    </button>
                </div>
            </div>

            <Show when={showCreateDialog()}>
                <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
                    <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content shadow-lg border-0 rounded-4">
                            <div class="modal-header border-bottom-0 pb-0">
                                <h5 class="modal-title fw-bold">Créer un point de restauration</h5>
                                <button type="button" class="btn-close" onClick={() => setShowCreateDialog(false)}></button>
                            </div>
                            <div class="modal-body py-4">
                                <div class="mb-3">
                                    <label for="snapshot-description" class="form-label fw-semibold">Description ou motif</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="snapshot-description"
                                        placeholder="Ex: Avant la dernière commission d'évaluation"
                                        value={snapshotDescription()}
                                        onInput={(e) => setSnapshotDescription(e.currentTarget.value)}
                                        autofocus
                                    />
                                </div>
                            </div>
                            <div class="modal-footer border-top-0 pt-0">
                                <button class="btn btn-light" onClick={() => setShowCreateDialog(false)}>Annuler</button>
                                <button class="btn btn-primary" onClick={createSnapshot}>Créer le snapshot</button>
                            </div>
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={loading()}>
                <div class="py-5 text-center">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Chargement...</span>
                    </div>
                    <div class="small text-muted mt-2">Chargement des versions...</div>
                </div>
            </Show>

            <Show when={restoring()}>
                <div class="alert alert-info d-flex align-items-center gap-2 mb-3" role="alert">
                    <div class="spinner-border spinner-border-sm" role="status"></div>
                    <span>Restauration en cours... La page se mettra à jour dans quelques instants.</span>
                </div>
            </Show>

            <Show when={!loading() && history().length === 0}>
                <div class="empty-state my-3">
                    <div class="empty-icon"><i class="bi bi-clock-history"></i></div>
                    <h5>Aucun point d'historique</h5>
                    <p>Les sauvegardes automatiques apparaîtront ici au fur et à mesure des modifications.</p>
                </div>
            </Show>

            <Show when={!loading() && history().length > 0}>
                <div class="table-responsive border rounded-3 mb-4">
                    <table class="table table-hover align-middle mb-0">
                        <thead class="table-light">
                            <tr>
                                <th>Date & Heure</th>
                                <th>Type</th>
                                <th>Auteur</th>
                                <th>Description</th>
                                <th>Taille</th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={history()}>
                                {(snapshot) => (
                                    <tr>
                                        <td class="fw-medium text-dark">{formatDate(snapshot.createdAt)}</td>
                                        <td>
                                            <span class={`badge rounded-pill ${getTypeBadgeClass(snapshot.type)}`}>
                                                {getTypeLabel(snapshot.type)}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="d-inline-flex align-items-center gap-1">
                                                <i class="bi bi-person text-muted"></i>
                                                {snapshot.createdBy || 'Système'}
                                            </span>
                                        </td>
                                        <td class="text-muted">{snapshot.description || '-'}</td>
                                        <td class="small text-muted">{Math.round(snapshot.dataSize / 1024)} KB</td>
                                        <td class="text-end">
                                            <button
                                                class="btn btn-outline-warning btn-sm d-inline-flex align-items-center gap-1"
                                                onClick={() => restoreSnapshot(snapshot.id)}
                                                disabled={restoring()}
                                                title="Restaurer ce snapshot"
                                            >
                                                <i class="bi bi-arrow-counterclockwise"></i>
                                                <span>Restaurer</span>
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </For>
                        </tbody>
                    </table>
                </div>
            </Show>

            <div class="p-3 bg-light rounded-3 border">
                <h6 class="fw-bold mb-2 d-flex align-items-center gap-2">
                    <i class="bi bi-info-circle text-primary"></i>
                    Informations sur les sauvegardes
                </h6>
                <ul class="small text-muted mb-0 ps-3">
                    <li>Des snapshots automatiques sont créés toutes les 10 minutes lors d'une activité.</li>
                    <li>Les 10 derniers snapshots automatiques et 20 snapshots manuels sont conservés.</li>
                    <li>Toute restauration crée automatiquement un point de sauvegarde préalable.</li>
                </ul>
            </div>
        </div>
    );
}
